import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Scale, Eye, EyeOff } from "lucide-react";
import { usePlans } from "@/hooks/usePlans";

export default function LoginPage({ mode: modeProp }) {
  const { precoDeEntrada } = usePlans();
  const navigate = useNavigate();
  const { invite_code } = useParams();
  const [searchParams] = useSearchParams();
  const { login, register, cadastrarComPagamento, user, loading: authLoading } = useAuth();

  const [isRegister, setIsRegister] = useState(modeProp === "register" || !!invite_code);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pedindoReset, setPedindoReset] = useState(false);

  // Quem chegou pelo botão de assinar segue para o checkout do plano escolhido;
  // os demais vão para o dashboard.
  const afterAuth = searchParams.get("next") === "checkout"
    ? `/plano?checkout=${searchParams.get("plan") || "pro_monthly"}`
    : "/dashboard";

  // Usuário autenticado (inclusive logo após login/registro) sai da tela.
  useEffect(() => {
    if (!authLoading && user) {
      navigate(afterAuth, { replace: true });
    }
  }, [user, authLoading, navigate, afterAuth]);

  useEffect(() => {
    if (searchParams.get("error") === "auth_failed") {
      toast.error("Falha na autenticação com Google. Tente novamente.");
    }
  }, [searchParams]);

  const pedirResetSenha = async () => {
    if (!email.trim()) return toast.error("Digite seu e-mail para receber o link.");
    setPedindoReset(true);
    try {
      await axios.post(`${API}/auth/password-reset/request`, { email: email.trim() }, { withCredentials: true });
      toast.success("Se houver conta com esse e-mail, enviamos o link para definir a senha.", { duration: 8000 });
    } catch {
      toast.error("Não foi possível enviar agora. Tente novamente em instantes.");
    } finally {
      setPedindoReset(false);
    }
  };

  const getErrorMessage = (err) => {
    const detail = err?.response?.data?.detail;
    if (!detail) return "Erro ao autenticar. Tente novamente.";
    if (typeof detail === "string") return detail;
    // Pydantic 422 returns array of {loc, msg, type}
    if (Array.isArray(detail)) {
      const first = detail[0];
      const field = first?.loc?.[first.loc.length - 1];
      const rawMsg = first?.msg || "";
      // Pydantic v2 custom validators prefix with "Value error, "
      const msg = rawMsg.replace(/^Value error,\s*/i, "");
      // Custom PT-BR messages from validators come through clean
      if (msg && !msg.toLowerCase().includes("@-sign") && !msg.toLowerCase().includes("valid email")) {
        return msg;
      }
      // Fallback by field
      if (field === "email") return "Email inválido.";
      if (field === "password") return "Senha deve ter pelo menos 6 caracteres.";
      if (field === "name") return "Nome é obrigatório.";
      return "Dados inválidos. Verifique os campos.";
    }
    return "Erro ao autenticar. Tente novamente.";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Frontend validation
    if (isRegister) {
      if (!name.trim()) {
        toast.error("Nome é obrigatório.");
        return;
      }
      if (password.length < 6) {
        toast.error("Senha deve ter pelo menos 6 caracteres.");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error("Email inválido.");
        return;
      }
    }

    setLoading(true);
    try {
      if (isRegister) {
        if (invite_code) {
          // Convidado entra no escritório de quem convidou: quem paga é o titular.
          await register(name, email, password, invite_code);
        } else {
          const { url } = await cadastrarComPagamento(name, email, password);
          window.location.assign(url);
          return;
        }
        toast.success("Conta criada com sucesso!");
      } else {
        await login(email, password);
        toast.success("Login realizado!");
      }
      navigate(afterAuth);
    } catch (err) {
      const detalhe = err?.response?.data?.detail;
      // Conta criada pelo Google não tem senha: a mensagem diz o caminho certo
      // em vez de deixar o usuário repetindo a senha.
      if (detalhe?.code === "google_account") {
        toast.error(detalhe.message, { duration: 8000 });
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REACT_APP_BACKEND_URL é vazio em produção → usa caminho relativo (nginx /api)
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const params = new URLSearchParams();
    if (invite_code) params.set("invite_code", invite_code);
    if (searchParams.get("next") === "checkout") {
      sessionStorage.setItem("tuto_after_auth", afterAuth);
    }
    const inviteParam = params.toString() ? `?${params}` : "";
    window.location.href = `${backendUrl}/api/auth/google/login${inviteParam}`;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage: `url(https://images.unsplash.com/photo-1623177623378-f6aa5fd70ba7?w=1200&q=80)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 hero-gradient opacity-80" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center">
            <img src="/logo-tuto.png" alt="TUTO by Kosmos" className="h-20 lg:h-24 w-auto brightness-0 invert" />
          </div>
          <div>
            <h2 className="text-white text-4xl font-bold leading-tight mb-4" style={{ fontFamily: "Cabinet Grotesk" }}>
              Gerencie seus processos com eficiência.
            </h2>
            <p className="text-blue-200 text-lg">
              Plataforma completa para advogados brasileiros. Processos, intimações e agenda em um só lugar.
            </p>
          </div>
        </div>
      </div>

      {/* Right panel - Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <img src="/logo-tuto.png" alt="TUTO by Kosmos" className="h-8 w-auto" />
          </div>

          <h1
            className="text-3xl font-bold text-[#001533] mb-2"
            style={{ fontFamily: "Cabinet Grotesk" }}
          >
            {!isRegister ? "Entrar" : invite_code ? "Criar conta" : "Assinar o TUTO"}
          </h1>
          <p className="text-[#335480] text-base mb-6">
            {!isRegister
              ? "Bem-vindo de volta, advogado(a)."
              : invite_code
                ? "Você foi convidado. Crie sua conta para acessar o escritório."
                : "Preencha seus dados e conclua o pagamento para começar."}
          </p>

          {/* O visitante precisa saber o preço e o que acontece a seguir antes
              de clicar. Sem isso, gente chegava esperando um plano grátis que
              não existe mais e parava numa tela de bloqueio. */}
          {isRegister && !invite_code && (
            <div className="rounded-lg border border-[#D1DEEE] bg-[#F4F7FB] p-4 mb-6" data-testid="resumo-do-plano">
              <div className="flex items-baseline justify-between gap-2 mb-2">
                <span className="font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
                  Plano START
                </span>
                <span className="text-xl font-bold text-[#003882]" style={{ fontFamily: "Cabinet Grotesk" }}>
                  {precoDeEntrada() || <span className="inline-block h-5 w-20 rounded bg-[#D1DEEE] animate-pulse align-middle" />}
                  <span className="text-sm font-medium text-[#335480]">/mês</span>
                </span>
              </div>
              <ul className="text-sm text-[#335480] space-y-1">
                <li>100 processos · 1 OAB · 2 usuários</li>
                <li>Intimações do DJEN automáticas</li>
                <li>Cancele quando quiser, sem multa</li>
              </ul>
            </div>
          )}

          {/* Google Auth */}
          <button
            data-testid="google-login-btn"
            onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 border border-[#D1DEEE] rounded-lg py-3 px-4 text-[#001533] font-medium hover:bg-[#F4F7FB] transition-colors mb-6 text-base"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {isRegister && !invite_code ? "Assinar com Google" : "Continuar com Google"}
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-[#D1DEEE]" />
            <span className="text-[#335480] text-sm">ou</span>
            <div className="flex-1 h-px bg-[#D1DEEE]" />
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="text-[#001533] font-medium text-base">
                  Nome completo
                </Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Dr. João Silva"
                  required
                  className="mt-1.5 h-12 text-base border-[#D1DEEE] focus:border-[#003882]"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-[#001533] font-medium text-base">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="joao@escritorio.com.br"
                required
                className="mt-1.5 h-12 text-base border-[#D1DEEE] focus:border-[#003882]"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-[#001533] font-medium text-base">
                  Senha
                </Label>
                {!isRegister && (
                  <button
                    type="button"
                    onClick={pedirResetSenha}
                    disabled={pedindoReset}
                    data-testid="esqueci-senha-btn"
                    className="text-[#003882] text-sm font-medium hover:underline"
                  >
                    {pedindoReset ? "Enviando..." : "Esqueci minha senha"}
                  </button>
                )}
              </div>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-12 text-base border-[#D1DEEE] focus:border-[#003882] pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#335480] hover:text-[#003882]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-12 text-base font-semibold bg-[#003882] hover:bg-[#00285e] text-white"
            >
              {loading
                ? "Aguarde..."
                : !isRegister
                  ? "Entrar"
                  : invite_code
                    ? "Criar conta"
                    : "Ir para o pagamento"}
            </Button>

            {isRegister && !invite_code && (
              <p className="text-xs text-[#6685AD] text-center" data-testid="aviso-pagamento">
                Você vai para o pagamento seguro. Sua conta é criada depois da confirmação —
                nada é cobrado sem você concluir.
              </p>
            )}
          </form>

          <p className="text-center text-[#335480] mt-6 text-base">
            {isRegister ? (
              <>
                Já tem conta?{" "}
                <button
                  data-testid="toggle-to-login-btn"
                  onClick={() => setIsRegister(false)}
                  className="text-[#003882] font-semibold hover:underline"
                >
                  Entrar
                </button>
              </>
            ) : (
              <>
                Não tem conta?{" "}
                <button
                  data-testid="toggle-to-register-btn"
                  onClick={() => setIsRegister(true)}
                  className="text-[#003882] font-semibold hover:underline"
                >
                  Assinar
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
